import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationEvent } from '../api/notifications/notification.types';
import { notificationsApi } from '../api/notifications/notifications';
import { useAuth } from './auth/auth-context';
import { clearSession } from '../api/api';
import { createReconnectingWs, type ReconnectingWs } from '../utils/reconnecting-ws';
import { WS_BASE_URL } from '../api/chat/chat';
import { isNative } from '../platform';
import { registerPush, getPushPermissionStatus } from '../platform/push';

/* ─── Reducer ─── */

type NotificationState = {
  items: NotificationEvent[];
  total: number;
  limit: number;
  offset: number;
  isLoading: boolean;
  notificationBootLoading: boolean;
};

type NotificationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_BOOT_LOADING'; payload: boolean }
  | { type: 'SET_NOTIFICATIONS'; payload: { items: NotificationEvent[]; total: number; limit: number; offset: number; reset: boolean } }
  | { type: 'ADD_REALTIME'; payload: NotificationEvent }
  | { type: 'MARK_READ'; payload: string }
  | { type: 'MARK_ALL_READ' }
  | { type: 'DISMISS'; payload: string };

const PAGE_SIZE = 10;

const initialState: NotificationState = {
  items: [],
  total: 0,
  limit: PAGE_SIZE,
  offset: 0,
  isLoading: false,
  notificationBootLoading: false,
};

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_BOOT_LOADING':
      return { ...state, notificationBootLoading: action.payload };

    case 'SET_NOTIFICATIONS': {
      const { items, total, limit, offset, reset } = action.payload;
      let merged: NotificationEvent[];
      if (reset) {
        // Preserve optimistic mark-read state: if MARK_READ was dispatched
        // before this reset fetch response arrived, the server may still
        // report is_read=false.  Keep the optimistic true so the UI doesn't
        // flicker back to "unread".
        const optimisticReadIds = new Set(
          state.items.filter((e) => e.is_read).map((e) => e.id),
        );
        merged = optimisticReadIds.size > 0
          ? items.map((e) =>
              !e.is_read && optimisticReadIds.has(e.id)
                ? { ...e, is_read: true, read_at: e.read_at ?? new Date().toISOString() }
                : e,
            )
          : items;
      } else {
        // Deduplicate: realtime items may already exist in the list
        const existingIds = new Set(state.items.map((e) => e.id));
        const fresh = items.filter((e) => !existingIds.has(e.id));
        merged = [...state.items, ...fresh];
      }
      return {
        ...state,
        items: merged,
        total,
        limit,
        offset,
        isLoading: false,
      };
    }

    case 'ADD_REALTIME': {
      const incoming = action.payload;
      if (state.items.some((e) => e.id === incoming.id)) return state;
      return {
        ...state,
        items: [incoming, ...state.items],
        total: state.total + 1,
      };
    }

    case 'MARK_READ': {
      const eventId = action.payload;
      return {
        ...state,
        items: state.items.map((e) =>
          e.id === eventId
            ? { ...e, is_read: true, read_at: e.read_at ?? new Date().toISOString() }
            : e
        ),
      };
    }

    case 'MARK_ALL_READ': {
      const nowIso = new Date().toISOString();
      return {
        ...state,
        items: state.items.map((e) => ({ ...e, is_read: true, read_at: e.read_at ?? nowIso })),
      };
    }

    case 'DISMISS': {
      const dismissId = action.payload;
      const filtered = state.items.filter((e) => e.id !== dismissId);
      return {
        ...state,
        items: filtered,
        total: Math.max(0, state.total - 1),
      };
    }

    default:
      return state;
  }
}

/* ─── Context ─── */

type NotificationsContextValue = {
  items: NotificationEvent[];
  unreadCount: number;
  total: number;
  isLoading: boolean;
  hasMore: boolean;
  notificationBootLoading: boolean;
  /** Set only for events arriving via WebSocket — never from boot fetch. */
  latestRealtimeEvent: NotificationEvent | null;

  fetchNotifications: (reset?: boolean) => Promise<void>;
  markRead: (eventId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (eventId: string) => Promise<void>;
  addRealtime: (event: NotificationEvent) => void;
  registerPushSubscription: () => Promise<void>;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { session, userId } = useAuth();
  const token = session?.access_token ?? null;
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(notificationReducer, initialState);

  const unreadCount = useMemo(() => {
    let n = 0;
    for (const e of state.items) if (!e.is_read) n += 1;
    return n;
  }, [state.items]);

  const hasMore = state.items.length < state.total;

  const [latestRealtimeEvent, setLatestRealtimeEvent] = useState<NotificationEvent | null>(null);

  const wsRef = useRef<ReconnectingWs | null>(null);

  /** Synchronous guard — prevents concurrent load-more calls from stacking
   *  (scroll events fire faster than React can process the isLoading state update). */
  const loadingRef = useRef(false);

  /** Prevents concurrent mark-all-read calls from spamming the API. */
  const markingAllRef = useRef(false);


  /* ─── Fetch notifications ─── */
  const fetchNotifications = useCallback(async (reset = false) => {
    // Allow reset calls to always go through, block concurrent load-more
    if (!reset && loadingRef.current) return;
    loadingRef.current = true;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const newOffset = reset ? 0 : state.offset + state.limit;
      const res = await notificationsApi.getNotifications({
        limit: state.limit,
        offset: reset ? 0 : newOffset,
      });
      dispatch({
        type: 'SET_NOTIFICATIONS',
        payload: {
          items: res.items,
          total: res.total,
          limit: res.limit,
          offset: res.offset,
          reset,
        },
      });
    } catch (err) {
      console.error('[NotificationContext] fetchNotifications failed:', err);
      dispatch({ type: 'SET_LOADING', payload: false });
    } finally {
      loadingRef.current = false;
    }
  }, [state.offset, state.limit]);

  /* ─── Mark read ─── */
  const markRead = useCallback(async (eventId: string) => {
    dispatch({ type: 'MARK_READ', payload: eventId });
    try {
      await notificationsApi.markRead(eventId);
    } catch {
      await fetchNotifications(true);
    }
  }, [fetchNotifications]);

  /* ─── Mark all read ─── */
  const markAllRead = useCallback(async () => {
    if (markingAllRef.current) return;
    markingAllRef.current = true;
    dispatch({ type: 'MARK_ALL_READ' });
    try {
      await notificationsApi.markAllRead();
      // Re-dispatch after API success to catch any items loaded by pagination
      // during the API call that may still appear as unread
      dispatch({ type: 'MARK_ALL_READ' });
    } catch {
      await fetchNotifications(true);
    } finally {
      markingAllRef.current = false;
    }
  }, [fetchNotifications]);

  /* ─── Dismiss (swipe-to-remove) ─── */
  const dismiss = useCallback(async (eventId: string) => {
    dispatch({ type: 'MARK_READ', payload: eventId });
    dispatch({ type: 'DISMISS', payload: eventId });
    try {
      await notificationsApi.markRead(eventId);
    } catch {
      // silent — item already removed from UI
    }
  }, []);

  /* ─── Add realtime event ─── */
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const addRealtime = useCallback((event: NotificationEvent) => {
    // Skip notifications where the current user is the actor (own messages)
    if (event.actor_id && event.actor_id === userIdRef.current) return;

    const normalized: NotificationEvent = {
      ...event,
      is_read: event.is_read ?? false,
      read_at: event.read_at ?? null,
    };
    dispatch({ type: 'ADD_REALTIME', payload: normalized });
    setLatestRealtimeEvent(normalized);
    // Mark-as-read is handled by IntersectionObserver in the notification panel
  }, []);


  /* ─── Register push subscription ─── */
  const registerPushSubscription = useCallback(async () => {
    console.log('[NotificationContext] registerPushSubscription() called');
    try {
      const result = await registerPush(); // platform module handles web vs native
      console.log('[NotificationContext] registerPush() returned:', result);
    } catch (err) {
      console.error('[NotificationContext] Push registration failed:', err);
    }
  }, []);

  /* ─── Initial fetch on boot when authed ─── */
  const bootFetchedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      bootFetchedRef.current = false;
      dispatch({ type: 'SET_BOOT_LOADING', payload: false });
      return;
    }

    if (bootFetchedRef.current) return;
    bootFetchedRef.current = true;

    let alive = true;
    dispatch({ type: 'SET_BOOT_LOADING', payload: true });

    notificationsApi
      .getNotifications({ limit: PAGE_SIZE, offset: 0 })
      .then((res) => {
        if (!alive) return;
        dispatch({
          type: 'SET_NOTIFICATIONS',
          payload: { items: res.items, total: res.total, limit: res.limit, offset: res.offset, reset: true },
        });
      })
      .catch((err) => {
        console.error('[NotificationContext] Boot fetch failed:', err);
        // Allow retry on next token change
        bootFetchedRef.current = false;
      })
      .finally(() => {
        if (alive) dispatch({ type: 'SET_BOOT_LOADING', payload: false });
      });

    return () => {
      alive = false;
    };
  }, [token]);

  /* ─── Auto push registration when authed + permission granted ─── */
  const pushRegisteredRef = useRef(false);

  // Re-check push registration periodically until it succeeds.
  // This covers the case where the NativePermissionsGate grants permission
  // AFTER the initial effect ran (first install flow).
  const pushRetryRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    console.log('[NotificationContext] push auto-reg effect, token:', token ? 'present' : 'absent', 'alreadyRegistered:', pushRegisteredRef.current);
    if (!token) {
      pushRegisteredRef.current = false;
      if (pushRetryRef.current) {
        clearInterval(pushRetryRef.current);
        pushRetryRef.current = null;
      }
      return;
    }
    if (pushRegisteredRef.current) return;

    const tryRegister = async () => {
      const status = await getPushPermissionStatus();
      console.log('[NotificationContext] push permission status:', status);
      if (status === 'granted') {
        console.log('[NotificationContext] permission granted, calling registerPushSubscription()');
        pushRegisteredRef.current = true;
        if (pushRetryRef.current) {
          clearInterval(pushRetryRef.current);
          pushRetryRef.current = null;
        }
        await registerPushSubscription();
      } else {
        console.log('[NotificationContext] push permission not granted, will retry');
      }
    };

    tryRegister();

    // On first install, permission may be granted after this effect runs
    // (NativePermissionsGate). Retry every 2s for up to 30s.
    if (!pushRegisteredRef.current && !pushRetryRef.current) {
      let retries = 0;
      pushRetryRef.current = setInterval(() => {
        retries++;
        if (pushRegisteredRef.current || retries >= 15) {
          if (pushRetryRef.current) {
            clearInterval(pushRetryRef.current);
            pushRetryRef.current = null;
          }
          return;
        }
        tryRegister();
      }, 2000);
    }

    return () => {
      if (pushRetryRef.current) {
        clearInterval(pushRetryRef.current);
        pushRetryRef.current = null;
      }
    };
  }, [token, registerPushSubscription]);

  /* ─── WebSocket: prepend new notifications as they arrive ─── */
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!token) return;

    const rws = createReconnectingWs({
      url: () => `${WS_BASE_URL}/ws/notifications?token=${tokenRef.current}`,
      serverPingPong: true,
      pingIntervalMs: 0, // server sends pings; we just pong
      onMessage: (evt: MessageEvent) => {
        try {
          const msg = JSON.parse(evt.data);

          if (msg?.type === 'session.revoked') {
            clearSession();
            return;
          }

          if (msg?.type !== 'notification.created') return;

          const incoming: NotificationEvent = msg.data ?? msg.payload;
          addRealtime(incoming);
        } catch {
          // ignore invalid frames
        }
      },
    });

    wsRef.current = rws;

    return () => {
      rws.dispose();
      wsRef.current = null;
    };
  }, [token, addRealtime]);

  /* ─── Stable ref for markRead so push-tap effect doesn't re-register listeners ─── */
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;

  /* ─── Listen for push notification taps (native) or SW messages (web) ─── */
  useEffect(() => {
    if (isNative()) {
      /** Resolve push notification data to a route URL. */
      const resolveUrl = (data: Record<string, string | undefined>): string => {
        if (data.event_type === 'custom') return '/notifications';
        if (data.conversation_id) return `/chat/${data.conversation_id}`;
        return '/chat';
      };

      const doNavigate = (url: string) => {
        // replace: true avoids polluting history; unique state ensures React Router
        // always triggers a re-render even when already on /chat/*
        navigate(url, { replace: true, state: { pushTap: Date.now() } });
      };

      /** Mark the tapped notification as read (if event_id is present in push data). */
      const markPushNotificationRead = (data: Record<string, string | undefined>) => {
        if (data.event_id) {
          // Optimistic dispatch happens synchronously inside markRead,
          // so the bell clears immediately even if the API call is delayed.
          markReadRef.current(data.event_id);
        }
      };

      // ── Cold start: check for pending push tap from MainActivity ──
      // On cold start, MainActivity.java forwards intent extras to a global
      // variable BEFORE React has mounted. Pick it up now.
      const pending = (window as any).__pendingPushTap as Record<string, string | undefined> | undefined;
      if (pending) {
        delete (window as any).__pendingPushTap;
        const url = resolveUrl(pending);
        markPushNotificationRead(pending);
        // Brief delay so React Router is fully ready
        setTimeout(() => doNavigate(url), 100);
      }

      // ── Warm start: listen for CustomEvent from MainActivity.onNewIntent ──
      const handlePushTap = (e: Event) => {
        const detail = ((e as CustomEvent).detail || {}) as Record<string, string | undefined>;
        markPushNotificationRead(detail);
        doNavigate(resolveUrl(detail));
      };
      window.addEventListener('pushNotificationTap', handlePushTap);

      // ── Fallback: Capacitor pushNotificationActionPerformed (in case a future
      //    update routes through Capacitor's notification builder) ──
      let capacitorCleanup: (() => void) | undefined;
      import('@capacitor/push-notifications').then(({ PushNotifications }) => {
        const listener = PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (notification) => {
            const data = notification.notification.data || {};
            markPushNotificationRead(data);
            doNavigate(resolveUrl(data));
          },
        );
        capacitorCleanup = () => { listener.then(h => h.remove()); };
      });

      return () => {
        window.removeEventListener('pushNotificationTap', handlePushTap);
        capacitorCleanup?.();
      };
    }

    // Web: existing SW message listener
    if (!('serviceWorker' in navigator)) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'NAVIGATE' && event.data.url) {
        const url = event.data.url as string;
        try { sessionStorage.setItem('push_redirect', url); } catch { /* ignore */ }
        navigate(url, { replace: true, state: { pushTap: Date.now() } });
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handler);
    };
  }, [navigate]);

  /* ─── Context value ─── */
  const value = useMemo<NotificationsContextValue>(
    () => ({
      items: state.items,
      unreadCount,
      total: state.total,
      isLoading: state.isLoading,
      hasMore,
      notificationBootLoading: state.notificationBootLoading,
      latestRealtimeEvent,
      fetchNotifications,
      markRead,
      markAllRead,
      dismiss,
      addRealtime,
      registerPushSubscription,
    }),
    [
      state.items,
      unreadCount,
      state.total,
      state.isLoading,
      hasMore,
      state.notificationBootLoading,
      latestRealtimeEvent,
      fetchNotifications,
      markRead,
      markAllRead,
      dismiss,
      addRealtime,
      registerPushSubscription,
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

/** Alias for app-providers compatibility */
export const NotificationProvider = NotificationsProvider;

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}
